import { useEffect, useState } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import HistoryModel from "../../UI/HistoryModel";
import FullHistoryModel from "../../UI/FullHistoryModal";
import Loader from "../../UI/Loader";
import { useTranslation } from "react-i18next";
import styles from "./SidebarInfo.module.css";
import { FaHistory, FaExclamationTriangle, FaListUl, FaCalendarAlt, FaChevronRight } from "react-icons/fa";

const PatientHistory = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [historyData, setHistoryData] = useState([]);
  const [allHistoryData, setAllHistoryData] = useState([]);
  const [selectedPatient, setselectedPatient] = useState(null);
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // 👈 loader state

  useEffect(() => {
    if (!props.PatientData || !props.PatientData.patientId) {
      navigate(-1); // Navigate back
    } else {
      fetchHistorygData();
    }
  }, [props.PatientData, navigate]);

  const fetchHistorygData = async () => {
    const id = props.PatientData.patientId.patientId;
    const token = getAuthToken();
    if (token === null || token === "EXPIRED") {
      navigate("/");
    }

    const apiChar = getApiChara();
    const apiUrl = `${apiChar}/patient/getPatientHistoryWithoutPrescriptionById/`;

    try {
      setIsLoading(true); // 👈 start loader
      const response = await fetch(apiUrl + id, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) {
        throw new Error("Could Not fetch details");
      }

      const data = await response.json();
      setHistoryData(data);
    } catch (error) {
      console.error("Error fetching waiting list data: ", error);
    } finally {
      setIsLoading(false); // 👈 stop loader
    }
  };

  const setSelectedDate = async (checkupId, dateTime) => {
    setSelectedDateTime(dateTime); // 👈 store selected dateTime
    const token = getAuthToken();
    if (token === null || token === "EXPIRED") {
      navigate("/");
    }
    const apiChar = getApiChara();
    const apiUrl = `${apiChar}/checkUp/getCheckUpRecordById?id=`;

    try {
      setIsLoading(true); // 👈 start loader
      const response = await fetch(apiUrl + checkupId, {
        method: "GET",
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) {
        throw new Error("could not fetch record");
      }

      const data = await response.json();
      setselectedPatient(data);
    } catch (error) {
      console.error("Error fetching record: ", error);
    } finally {
      setIsLoading(false); // 👈 stop loader
    }
  };

  const fetchAllHistoryData = async () => {
    const id = props.PatientData.patientId.patientId;
    const token = getAuthToken();
    if (token === null || token === "EXPIRED") {
      navigate("/");
    }

    const apiChar = getApiChara();
    const apiUrl = `${apiChar}/patient/getPatientHistoryById/`;

    try {
      setIsLoading(true); // 👈 start loader
      const response = await fetch(apiUrl + id, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) {
        throw new Error("Could Not fetch details");
      }

      const data = await response.json();
      setAllHistoryData(data);
    } catch (error) {
      console.error("Error fetching full history data: ", error);
    } finally {
      setIsLoading(false); // 👈 stop loader
    }
  };

  const onDataCloseHandler = () => {
    navigate(".");
    setselectedPatient(null);
  };

  const onHistoryDataCloseHandler = () => {
    navigate(".");
    setAllHistoryData([]);
  };

  return (
    <>
      {isLoading && <Loader />} {/* 👈 loader display */}

      {selectedPatient && (
        <HistoryModel
          selectedPatient={selectedPatient}
          OnCloseModalBlock={onDataCloseHandler}
          selectedDateTime={selectedDateTime} // 👈 pass dateTime
          onGetHistoryModelData={props.onGetHistoryModelData}
          onDeleteSuccess={fetchHistorygData} // 👈 refresh sidebar list after delete
        />
      )}

      {allHistoryData && allHistoryData.length > 0 && (
        <FullHistoryModel
          allHistoryData={allHistoryData}
          OnCloseModalBlock={onHistoryDataCloseHandler}
          selectedDateTime={selectedDateTime} // 👈 pass dateTime
          onGetHistoryModelData={props.onGetHistoryModelData}
          onDeleteSuccess={() => {
            fetchHistorygData();
            fetchAllHistoryData();
          }} // 👈 refresh both after delete in full history
        />
      )}

      <div>
        {(() => {
          // Extract all allergy values (filter out falsy or empty strings)
          const allergies = historyData
            ?.map((item) => item.allergy?.trim())
            .filter((a) => a && a !== "-");

          // Remove duplicates
          const uniqueAllergies = [...new Set(allergies)];

          // Only render if at least one valid allergy exists
          if (uniqueAllergies.length === 0) return null;

          return (
            <div className={styles.allergyBox}>
              <FaExclamationTriangle className={styles.allergyIcon} />
              <p className={styles.allergyText}>
                <strong>{t("Allergy")}:</strong> {uniqueAllergies.join(", ")}
              </p>
            </div>
          );
        })()}

        <div className={styles.sidebarCard}>
          <div className={styles.sidebarHeader}>
            <FaHistory /> {t("Medical History")}
          </div>
          <div className={styles.sidebarContent}>
            <button className={`${styles.premiumButton} ${styles.primaryAction}`} onClick={fetchAllHistoryData}>
              <FaListUl /> {t("Full History")}
            </button>
            <div className={styles.historyList}>
              {historyData.map((item, index) => (
                <div className={styles.historyItem} key={index}>
                  <button
                    className={styles.historyButton}
                    onClick={() => setSelectedDate(item.checkUpId, item.dateTime)}
                  >
                    <span><FaCalendarAlt className="me-2 text-muted" size={12} /> {item.dateTime}</span>
                    <FaChevronRight size={10} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PatientHistory;
